import { useState, useEffect } from 'react';
import {
  getParts,
  createPart,
  updatePart,
  deletePart,
  getMachines,
  createOperation,
  updateOperation,
  deleteOperation,
} from '../../services/erpApi';

export default function PartsPage() {
  const [parts, setParts] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPart, setExpandedPart] = useState(null);
  
  // Part form state
  const [showPartForm, setShowPartForm] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [partFormData, setPartFormData] = useState({
    partNumber: '',
    name: '',
    description: '',
  });

  // Operation form state
  const [showOpForm, setShowOpForm] = useState(false);
  const [editingOp, setEditingOp] = useState(null);
  const [opFormData, setOpFormData] = useState({
    sequence: '',
    name: '',
    cycleTime: '',
    setupTime: '',
    machineId: '',
    description: '',
  });
  const [opPartId, setOpPartId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [partsData, machinesData] = await Promise.all([
        getParts(),
        getMachines(),
      ]);
      setParts(partsData);
      setMachines(machinesData);
      setError(null);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Part handlers
  const handlePartSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPart) {
        await updatePart(editingPart.id, partFormData);
      } else {
        await createPart(partFormData);
      }
      setShowPartForm(false);
      setEditingPart(null);
      setPartFormData({ partNumber: '', name: '', description: '' });
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save part');
    }
  };

  const handleEditPart = (part) => {
    setEditingPart(part);
    setPartFormData({
      partNumber: part.partNumber,
      name: part.name,
      description: part.description || '',
    });
    setShowPartForm(true);
  };

  const handleDeletePart = async (id) => {
    if (!confirm('Are you sure? This will delete all operations for this part.')) return;
    try {
      await deletePart(id);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete part');
    }
  };

  // Operation handlers
  const handleAddOperation = (partId) => {
    setOpPartId(partId);
    setEditingOp(null);
    // Suggest next sequence number
    const part = parts.find(p => p.id === partId);
    const maxSeq = part?.operations?.reduce((max, op) => Math.max(max, op.sequence), 0) || 0;
    setOpFormData({
      sequence: String(maxSeq + 10),
      name: '',
      cycleTime: '',
      setupTime: '',
      machineId: '',
      description: '',
    });
    setShowOpForm(true);
  };

  const handleEditOperation = (partId, operation) => {
    setOpPartId(partId);
    setEditingOp(operation);
    setOpFormData({
      sequence: String(operation.sequence),
      name: operation.name,
      cycleTime: String(operation.cycleTime),
      setupTime: String(operation.setupTime),
      machineId: operation.machineId,
      description: operation.description || '',
    });
    setShowOpForm(true);
  };

  const handleOpSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOp) {
        await updateOperation(editingOp.id, opFormData);
      } else {
        await createOperation(opPartId, opFormData);
      }
      setShowOpForm(false);
      setEditingOp(null);
      setOpPartId(null);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save operation');
    }
  };

  const handleDeleteOperation = async (opId) => {
    if (!confirm('Delete this operation?')) return;
    try {
      await deleteOperation(opId);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete operation');
    }
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  if (loading) {
    return <div className="text-gray-400">Loading parts...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Parts & Routing</h2>
        <button
          onClick={() => setShowPartForm(true)}
          className="px-4 py-2 bg-green-600 hover:bg-green-500 rounded text-white"
        >
          + Add Part
        </button>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-700 rounded p-3 text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Part Form Modal */}
      {showPartForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingPart ? 'Edit Part' : 'Add Part'}
            </h3>
            <form onSubmit={handlePartSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Part Number *</label>
                <input
                  type="text"
                  value={partFormData.partNumber}
                  onChange={(e) => setPartFormData({ ...partFormData, partNumber: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-green-500 focus:outline-none"
                  required
                  placeholder="e.g., 100001 or PN 100001 REV A"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name *</label>
                <input
                  type="text"
                  value={partFormData.name}
                  onChange={(e) => setPartFormData({ ...partFormData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-green-500 focus:outline-none"
                  required
                  placeholder="e.g., Shaft Assembly"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <textarea
                  value={partFormData.description}
                  onChange={(e) => setPartFormData({ ...partFormData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-green-500 focus:outline-none"
                  rows={2}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-500 rounded text-white"
                >
                  {editingPart ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPartForm(false);
                    setEditingPart(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded text-white"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Operation Form Modal */}
      {showOpForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingOp ? 'Edit Operation' : 'Add Operation'}
            </h3>
            <form onSubmit={handleOpSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Sequence *</label>
                  <input
                    type="number"
                    value={opFormData.sequence}
                    onChange={(e) => setOpFormData({ ...opFormData, sequence: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-amber-500 focus:outline-none"
                    required
                    min="1"
                    step="10"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Machine *</label>
                  <select
                    value={opFormData.machineId}
                    onChange={(e) => setOpFormData({ ...opFormData, machineId: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-amber-500 focus:outline-none"
                    required
                  >
                    <option value="">Select machine...</option>
                    {machines.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} {m.machineType ? `(${m.machineType})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Operation Name *</label>
                <input
                  type="text"
                  value={opFormData.name}
                  onChange={(e) => setOpFormData({ ...opFormData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-amber-500 focus:outline-none"
                  required
                  placeholder="e.g., Rough Turn OD"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Cycle Time (sec) *</label>
                  <input
                    type="number"
                    value={opFormData.cycleTime}
                    onChange={(e) => setOpFormData({ ...opFormData, cycleTime: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-amber-500 focus:outline-none"
                    required
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Setup Time (sec) *</label>
                  <input
                    type="number"
                    value={opFormData.setupTime}
                    onChange={(e) => setOpFormData({ ...opFormData, setupTime: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-amber-500 focus:outline-none"
                    required
                    min="0"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <textarea
                  value={opFormData.description}
                  onChange={(e) => setOpFormData({ ...opFormData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-amber-500 focus:outline-none"
                  rows={2}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded text-white"
                >
                  {editingOp ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowOpForm(false);
                    setEditingOp(null);
                    setOpPartId(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded text-white"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Parts List */}
      <div className="space-y-4">
        {parts.length === 0 ? (
          <div className="bg-gray-800 rounded-lg p-8 text-center text-gray-500">
            No parts yet. Click "Add Part" to create one.
          </div>
        ) : (
          parts.map((part) => (
            <div key={part.id} className="bg-gray-800 rounded-lg overflow-hidden">
              {/* Part Header */}
              <div
                className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-700/50"
                onClick={() => setExpandedPart(expandedPart === part.id ? null : part.id)}
              >
                <div className="flex items-center gap-4">
                  <span className="text-xl">{expandedPart === part.id ? '▼' : '▶'}</span>
                  <div>
                    <span className="font-mono text-amber-400 mr-3">{part.partNumber}</span>
                    <span className="font-medium">{part.name}</span>
                    {part.description && (
                      <span className="text-gray-500 ml-2">— {part.description}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-400">
                    {part.operations?.length || 0} ops · {part._count?.workOrders || 0} WOs
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditPart(part);
                    }}
                    className="text-blue-400 hover:text-blue-300"
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePart(part.id);
                    }}
                    className="text-red-400 hover:text-red-300"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Operations (Routing) */}
              {expandedPart === part.id && (
                <div className="border-t border-gray-700 px-4 py-3 bg-gray-800/50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-300">Routing (Operations)</h4>
                    <button
                      onClick={() => handleAddOperation(part.id)}
                      className="text-sm px-3 py-1 bg-amber-600 hover:bg-amber-500 rounded text-white"
                    >
                      + Add Operation
                    </button>
                  </div>
                  
                  {part.operations?.length === 0 ? (
                    <p className="text-gray-500 text-sm">No operations defined. Add operations to define the routing.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-400 text-left">
                          <th className="pb-2 w-16">Seq</th>
                          <th className="pb-2">Operation</th>
                          <th className="pb-2">Machine</th>
                          <th className="pb-2 w-24">Cycle</th>
                          <th className="pb-2 w-24">Setup</th>
                          <th className="pb-2 w-24 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {part.operations.map((op) => (
                          <tr key={op.id} className="border-t border-gray-700">
                            <td className="py-2 font-mono text-gray-400">{op.sequence}</td>
                            <td className="py-2">{op.name}</td>
                            <td className="py-2 text-gray-400">
                              {op.machine?.name || 'Unknown'}
                            </td>
                            <td className="py-2 text-gray-400">{formatTime(op.cycleTime)}</td>
                            <td className="py-2 text-gray-400">{formatTime(op.setupTime)}</td>
                            <td className="py-2 text-right">
                              <button
                                onClick={() => handleEditOperation(part.id, op)}
                                className="text-blue-400 hover:text-blue-300 mr-2"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteOperation(op.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
